import React from 'react'

import { Gauge, makeAppStyles, ProviderLogo } from '@percept/mui'

import { ArrowDownward, ArrowUpward } from '@percept/mui/icons'

import { ChartData, SVGDatumType } from '@percept/mui/charts'

import { displayNumber } from '@percept/utils'

import { providerLabelMap } from '@percept/constants'

import { ReportProviderV2 } from '@percept/types'


export type ProviderChartData = ChartData<
  SVGDatumType
  & {
    provider: ReportProviderV2
    previousValue: number | null
  }
>

export type BreakdownValueFormatter = (value: number) => React.ReactNode

export type ProviderBreakdownProps = {
  chartData: ProviderChartData
  overallTotal: number
  previousOverallTotal: number
  headlineContent?: React.ReactNode
  formatter?: BreakdownValueFormatter
  sentiment?: 'UP_POSITIVE' | 'UP_NEGATIVE' | null
}


const useStyles = makeAppStyles( theme => ({
  overallContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 40,
    marginBottom: theme.spacing(3),
  },
  overallContentRight: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(3),
  },
  overallTotal: {
    fontSize: 32,
    fontWeight: theme.typography.fontWeightBold,
    [theme.breakpoints.down('md')]: {
      fontSize: 28,
    },
  },
  changeIcon: {
    marginLeft: theme.spacing(0.5),
    position: 'relative',
    top: -1,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
    fontSize: 16,
    height: 20,
    fontWeight: theme.typography.fontWeightBold,
    [theme.breakpoints.up('xl')]: {
      height: 24,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
    },
  },
  providerTotal: {
  },
  marker: {
    marginRight: theme.spacing(1),
  },
  providerLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  value: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 18,
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
    },
  },
  positive: {
    color: theme.chart.healthColourScale(1),
  },
  negative: {
    color: theme.chart.healthColourScale(0),
  },
}))


export const ProviderBreakdown = ({
  headlineContent,
  chartData,
  overallTotal,
  previousOverallTotal,
  formatter = displayNumber,
  sentiment,
}: ProviderBreakdownProps): JSX.Element => {
  const classes = useStyles()

  let trendUpClassName = classes.changeIcon, trendDownClassName = classes.changeIcon
  if( sentiment === 'UP_NEGATIVE' ){
    trendUpClassName += (' ' + classes.negative)
    trendDownClassName += (' ' + classes.positive)
  }else if( sentiment === 'UP_POSITIVE' ){
    trendUpClassName += (' ' + classes.positive)
    trendDownClassName += (' ' + classes.negative)
  }

  return (
    <>
    <div className={classes.overallContainer}>
      { headlineContent || (
        <Gauge
          animate={false}
          height='1.75em'
          data={chartData}
          segmentPadding={1}
          orientation='horizontal' />
      )}

      <div className={classes.overallContentRight}>
        <span className={classes.overallTotal}>
          { formatter(overallTotal) }
        </span>
        {
          previousOverallTotal < overallTotal ?
            <ArrowUpward fontSize='large' className={trendUpClassName} /> :
            <ArrowDownward fontSize='large' className={trendDownClassName} />
        }
      </div>
    </div>
      
      { chartData.map( d => (
        <div
          key={d.provider}
          className={classes.row}>
          <div className={classes.providerLabel}>
            <svg className={classes.marker} width={16} height={16} viewBox='0 0 1 1'>
              <circle fill={d.color} cx={0.5} cy={0.5} r={0.5} />
            </svg>
            { providerLabelMap[d.provider] }
          </div>
          {/* <ProviderLogo
            provider={d.provider}
            color={d.color}
            size={1.15} /> */}
          <div className={classes.value}>
            { formatter(d.value || 0) }
            {
              (d.previousValue || 0) < (d.value || 0) ?
                <ArrowUpward fontSize='default' className={trendUpClassName} /> :
                <ArrowDownward fontSize='default' className={trendDownClassName} />
            }
          </div>
        </div>
      ))}
    </>
  )
}
